import React, { useEffect, useRef, useState } from 'react';

import { formatMoney } from '../onboardingV2.locale';
import {
    formatTierQuantityRange,
    getTierDiscountValueError,
    getTierMinQuantityError,
    sortTiersByMinQuantity,
} from '../../../constants/pricingTierConfig';
import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import StepHeader from '../components/StepHeader/StepHeader';
import { TextField } from '../components/Field/Field';
import { Button, TextButton } from '../components/Button/Button';
import Callout from '../components/Callout/Callout';
import {
    WHOLESALE_DISCOUNT_TYPE,
    WHOLESALE_MAX_LEVELS,
    createWholesaleRow,
    discountedUnitPrice,
    filledRows,
    isDiscountTooHigh,
    validateWholesaleRows,
    wholesaleThresholds,
} from '../onboardingV2.wholesale';
import { AlertSlot, Center } from './steps.styles';
import {
    ScopeNote,
    Levels,
    LevelCard,
    LevelHead,
    LevelFields,
    LevelMeta,
    RemoveLevel,
    AddLevel,
} from './WholesaleStep.styles';

/** Example values shown as placeholders, one pair per level. */
const PLACEHOLDERS = [
    ['12', '10'],
    ['50', '15'],
    ['100', '20'],
    ['200', '25'],
    ['500', '30'],
];

const onlyDigits = (value, max) => String(value || '').replace(/\D/g, '').slice(0, max);

/**
 * Step "precios-mayor" (wholesale / both only, optional): volume discounts by quantity. Saved as a
 * store-wide percentage rule in the CMS pricing tiers (created after the account exists, see
 * finalizeOnboardingV2), so it also applies to the products added later.
 */
const WholesaleStep = ({ ctx }) => {
    const {
        t,
        isDemo,
        analytics,
        onboarding,
        updateOnboarding,
        productDraft,
        goTo,
        trackStep,
        stepNumberOf,
        totalSteps,
    } = ctx;

    const { currency, moneyLocale } = ctx;
    const [rows, setRows] = useState(() =>
        Array.isArray(onboarding.wholesaleTiers) && onboarding.wholesaleTiers.length
            ? onboarding.wholesaleTiers
            : [createWholesaleRow()]
    );
    const [submitted, setSubmitted] = useState(false);
    const [formError, setFormError] = useState(null);
    const alertRef = useRef(null);

    useEffect(() => {
        if (formError && alertRef.current) alertRef.current.focus();
    }, [formError]);

    // Persist on every change so the live preview shows the first level while typing.
    const persistRows = (next) => {
        setRows(next);
        setFormError(null);
        updateOnboarding({ wholesaleTiers: next });
    };

    const updateRow = (localId, patch) =>
        persistRows(rows.map((row) => (row.localId === localId ? { ...row, ...patch } : row)));

    const addLevel = () => {
        if (rows.length >= WHOLESALE_MAX_LEVELS) return;
        persistRows([...rows, createWholesaleRow()]);
    };

    const removeLevel = (localId) => {
        if (rows.length <= 1) return;
        persistRows(rows.filter((row) => row.localId !== localId));
    };

    // Example rows use the first product added (or the one still in the form).
    const exampleProduct = (onboarding.products || [])[0] || productDraft;
    const basePrice = Number(exampleProduct.price) > 0 ? Number(exampleProduct.price) : null;
    const productName = (exampleProduct.name || '').trim();
    const sortedFilled = sortTiersByMinQuantity(filledRows(rows));

    const rowErrors = (row) => {
        if (!submitted) return {};
        const index = sortedFilled.findIndex((r) => r.localId === row.localId);
        if (index === -1) return {};
        return {
            min: getTierMinQuantityError(sortedFilled, index),
            discount:
                getTierDiscountValueError(sortedFilled, index, WHOLESALE_DISCOUNT_TYPE) ||
                (isDiscountTooHigh(row.discount_value) ? t('SignUpV2.mayor.discountMax') : null),
        };
    };

    const rowMeta = (row) => {
        const index = sortedFilled.findIndex((r) => r.localId === row.localId);
        const range = index === -1 ? '' : formatTierQuantityRange(sortedFilled, index);
        const pct = Number(row.discount_value);
        const unit = basePrice && pct > 0 && pct <= 100 ? discountedUnitPrice(basePrice, pct) : null;
        const priceText =
            unit !== null
                ? productName
                    ? t('SignUpV2.mayor.unitPriceExample', {
                          name: productName,
                          price: formatMoney(unit, currency, moneyLocale),
                      })
                    : t('SignUpV2.mayor.unitPrice', { price: formatMoney(unit, currency, moneyLocale) })
                : '';
        return { range, priceText };
    };

    const onSubmit = (event) => {
        event.preventDefault();
        setSubmitted(true);
        const check = validateWholesaleRows(rows);
        if (!check.valid) {
            setFormError(
                check.empty
                    ? t('SignUpV2.mayor.emptyError')
                    : check.messageKey
                    ? t(check.messageKey)
                    : check.message
            );
            analytics.track('signup_validation_error', {
                step_name: 'precios-mayor',
                field: 'tiers',
                error_code: check.empty ? 'empty' : 'invalid',
            });
            return;
        }
        const thresholds = wholesaleThresholds(rows);
        updateOnboarding({
            wholesaleTiers: rows,
            wholesaleSkipped: false,
        });
        trackStep('signup_step_completed', {
            levels: thresholds.length,
            max_discount: thresholds[thresholds.length - 1].discount_value,
        });
        goTo('whatsapp');
    };

    const onLater = () => {
        updateOnboarding({ wholesaleSkipped: true });
        trackStep('signup_step_completed', { skipped: true });
        goTo('whatsapp');
    };

    const onBack = () => {
        ctx.noteBack();
        goTo('producto');
    };

    return (
        <OnboardingLayout
            preview={ctx.preview}
            step={stepNumberOf('precios-mayor')}
            total={totalSteps}
            stepKey="precios-mayor"
            title={t('SignUpV2.mayor.title')}
            headingId="mayor-title"
            isDemo={isDemo}
            onBack={onBack}
            onSubmit={onSubmit}
            footer={
                <>
                    <Button type="submit" iconRight="ri-arrow-right-line">
                        {t('SignUpV2.mayor.cta')}
                    </Button>
                    <Center>
                        <TextButton tone="muted" onClick={onLater}>
                            {t('SignUpV2.mayor.later')}
                        </TextButton>
                    </Center>
                </>
            }
        >
            <StepHeader
                id="mayor-title"
                title={t('SignUpV2.mayor.title')}
                subtitle={t('SignUpV2.mayor.subtitle')}
            />
            <ScopeNote>
                <i className="ri-store-2-line" aria-hidden="true" />
                <span>{t('SignUpV2.mayor.scope')}</span>
            </ScopeNote>
            <AlertSlot>
                {formError && (
                    <Callout variant="error" ref={alertRef}>
                        <p>{formError}</p>
                    </Callout>
                )}
            </AlertSlot>

            <Levels>
                {rows.map((row, index) => {
                    const errors = rowErrors(row);
                    const { range, priceText } = rowMeta(row);
                    const [minPh, pctPh] = PLACEHOLDERS[index] || PLACEHOLDERS[PLACEHOLDERS.length - 1];
                    return (
                        <LevelCard key={row.localId}>
                            <LevelHead>
                                <span>{t('SignUpV2.mayor.level', { n: index + 1 })}</span>
                                {rows.length > 1 && (
                                    <RemoveLevel
                                        type="button"
                                        onClick={() => removeLevel(row.localId)}
                                        aria-label={t('SignUpV2.mayor.removeLevel', { n: index + 1 })}
                                    >
                                        <i className="ri-delete-bin-line" aria-hidden="true" />
                                    </RemoveLevel>
                                )}
                            </LevelHead>
                            <LevelFields>
                                <TextField
                                    id={`mayor-min-${row.localId}`}
                                    label={t('SignUpV2.mayor.fromLabel')}
                                    placeholder={minPh}
                                    type="text"
                                    inputMode="numeric"
                                    value={row.min_quantity}
                                    error={errors.min || null}
                                    onChange={(e) =>
                                        updateRow(row.localId, { min_quantity: onlyDigits(e.target.value, 6) })
                                    }
                                />
                                <TextField
                                    id={`mayor-pct-${row.localId}`}
                                    label={t('SignUpV2.mayor.discountLabel')}
                                    placeholder={pctPh}
                                    type="text"
                                    inputMode="numeric"
                                    value={row.discount_value}
                                    error={errors.discount || null}
                                    onChange={(e) =>
                                        updateRow(row.localId, { discount_value: onlyDigits(e.target.value, 3) })
                                    }
                                />
                            </LevelFields>
                            {(range || priceText) && (
                                <LevelMeta>
                                    {range && <span>{range}</span>}
                                    {priceText && <strong>{priceText}</strong>}
                                </LevelMeta>
                            )}
                        </LevelCard>
                    );
                })}
            </Levels>

            {rows.length < WHOLESALE_MAX_LEVELS ? (
                <AddLevel type="button" onClick={addLevel}>
                    <i className="ri-add-line" aria-hidden="true" />
                    {t('SignUpV2.mayor.addLevel')}
                </AddLevel>
            ) : (
                <LevelMeta as="p">{t('SignUpV2.mayor.maxLevels', { count: WHOLESALE_MAX_LEVELS })}</LevelMeta>
            )}
        </OnboardingLayout>
    );
};

export default WholesaleStep;
